import React, { useState, useCallback, useMemo, useEffect, useContext } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { ThemeContext } from '../contexts/ThemeContext';
import { useSettings } from '../contexts/SettingsContext';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import apiService from '../services/apiService';
import { 
  BarChart3, 
  Package, 
  Users, 
  FileText, 
  ShoppingCart,
  TrendingDown,
  TrendingUp,
  CreditCard,
  DollarSign,
  Bell,
  Settings,
  Search,
  Calendar,
  ChevronDown,
  ChevronRight,
  Menu,
  Activity,
  PlusCircle,
  User,
  LogOut,
  RefreshCw,
  Filter,
  PanelLeftClose,
  PanelLeftOpen,
  Moon,
  Sun,
  ArrowUpRight, 
  ArrowDownRight,
  Clock,
  X,
  Plus,
  Receipt,
  ClipboardList,
  Eye,
  Edit,
  Trash2,
  Download,
  Send,
  CheckCircle,
  Check,
  AlertCircle,
  Info,
  Loader2,
  Tag,
  Box,
  Package2,
  Layers,
  Calculator
} from 'lucide-react';
import toast from 'react-hot-toast';

// Quantity formatting utility function
const formatQuantity = (quantity) => {
  const num = parseFloat(quantity || 0);
  if (num >= 0 && num <= 9 && num % 1 === 0) {
    return num.toString(); // Show single digit for 0-9 whole numbers
  }
  // For decimals, always show 2 decimal places
  if (num % 1 !== 0) {
    return num.toFixed(2);
  }
  // For whole numbers > 9, show without decimals
  return num.toString();
};

const ProductList = () => {
  
  const { logout, user } = useAuth();
  const { theme, toggleTheme } = useContext(ThemeContext);
  const { formatCurrency, currencySymbol } = useSettings();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(true);
  const [activeDropdown, setActiveDropdown] = useState(location.pathname.startsWith('/product') ? 2 : null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  
  // Product state management
  const [loading, setLoading] = useState(false);
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [productStats, setProductStats] = useState({});
  const [pagination, setPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalItems: 0,
    itemsPerPage: 15
  });
  
  // Filter states
  const [filters, setFilters] = useState({
    category: 'all',
    status: 'all',
    search: '',
    sort_by: 'name',
    sort_order: 'asc'
  });

  // Add Product Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [addProductLoading, setAddProductLoading] = useState(false);
  const [addProductForm, setAddProductForm] = useState({
    name: '',
    productID: '',
    description: '',
    image_file: null,
    image_preview: '',
    category_id: '',
    supplier_id: '',
    cost_price: '',
    selling_price_pcs: '',
    selling_price_ctn: '',
    quantity: '',
    reorder_level: '',
    unit_of_measure: 'pcs',
    pieces_in_ctn: '',
    vat_type: 'inclusive',
    vat_rate: '0',
    standard_margin: '',
    current_margin: ''
  });
  const [formErrors, setFormErrors] = useState({});
  
  // Add validation state for product name
  const [validationErrors, setValidationErrors] = useState({});
  const [isValidatingName, setIsValidatingName] = useState(false);

  // Product action modals state
  const [showViewModal, setShowViewModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [deleteProductLoading, setDeleteProductLoading] = useState(false);
  const [isEditMode, setIsEditMode] = useState(false);

  // Image Preview Modal states
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);

  // Product ID sequence tracking
  const [productIdSequence, setProductIdSequence] = useState({});

  // Navigation items
  const navigationItems = useMemo(() => [
    {
      name: 'Dashboard',
      href: '/dashboard',
      icon: BarChart3,
      active: location.pathname === '/dashboard'
    },
    {
      name: 'POS Operations',
      icon: CreditCard,
      children: [
        { name: 'Retail POS', href: '/retail-pos', active: location.pathname === '/retail-pos' },
        { name: 'Bulk Sales', href: '/bulk-sales', active: location.pathname === '/bulk-sales' },
        { name: 'Proforma Invoice', href: '/proforma-invoice', active: location.pathname === '/proforma-invoice' },
        { name: 'Credit Note', href: '/credit-note', active: location.pathname === '/credit-note' }
      ]
    },
    {
      name: 'Products',
      icon: Package,
      children: [
        { name: 'Product List', href: '/products', active: location.pathname === '/products' },
        { name: 'Categories', href: '/categories', active: location.pathname === '/categories' },
        { name: 'Stock Management', href: '/stock', active: location.pathname === '/stock' }
      ]
    },
    {
      name: 'Customers',
      icon: Users,
      children: [
        { name: 'Customer List', href: '/customers', active: location.pathname === '/customers' },
        { name: 'Customer Payments', href: '/customer-payments', active: location.pathname === '/customer-payments' }
      ]
    },
    {
      name: 'Suppliers',
      icon: Users,
      children: [
        { name: 'Supplier List', href: '/suppliers', active: location.pathname === '/suppliers' },
        { name: 'Supplier Payments', href: '/supplier-payments', active: location.pathname === '/supplier-payments' }
      ]
    },
    {
      name: 'Purchases',
      icon: ShoppingCart,
      children: [
        { name: 'Purchase List', href: '/purchases', active: location.pathname === '/purchases' },
        { name: 'Return Purchases', href: '/purchases/return', active: location.pathname === '/purchases/return' },
        { name: 'Receive Purchases', href: '/purchases/receive', active: location.pathname === '/purchases/receive' }
      ]
    },
    {
      name: 'Accounts',
      icon: Calculator,
      children: [
        { name: 'Expenses', href: '/expenses', active: location.pathname === '/expenses' },
        { name: 'Cash Analysis', href: '/cash-analysis', active: location.pathname === '/cash-analysis' }
      ]
    },
    {
      name: 'Reports',
      icon: TrendingUp,
      children: [
        { name: 'Sales Report', href: '/sales-report', active: location.pathname === '/sales-report' },
        { name: 'Inventory Report', href: '/inventory-report', active: location.pathname === '/inventory-report' },
        { name: 'Financial Report', href: '/financial-report', active: location.pathname === '/financial-report' }
      ]
    },
    {
      name: 'Settings',
      icon: Settings,
      children: [
        { name: 'General Settings', href: '/settings', active: location.pathname === '/settings' },
        { name: 'User Management', href: '/users', active: location.pathname === '/users' }
      ]
    }
  ], [location.pathname]);

  const handleCardClick = useCallback((href) => {
    navigate(href);
  }, [navigate]);

  const handleFilterChange = useCallback((field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
    // Reset to first page when filters change
    setPagination(prev => ({ ...prev, currentPage: 1 }));
  }, []);

  // Fetch real data from API
  const fetchProducts = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.getProducts({
        search: filters.search,
        category_id: filters.category !== 'all' ? filters.category : undefined,
        status: filters.status !== 'all' ? filters.status : undefined,
        page: pagination.currentPage,
        per_page: pagination.itemsPerPage,
        sort_by: filters.sort_by,
        sort_order: filters.sort_order
      });
      
      
      // Handle the new pagination response format
      const productsData = response.data || [];
      
      setProducts(productsData);
      
      // Update pagination with the new format
      if (response.pagination) {
        setPagination(prev => ({
          ...prev,
          currentPage: response.pagination.current_page || 1,
          totalPages: response.pagination.last_page || 1,
          totalItems: response.pagination.total || 0,
          itemsPerPage: response.pagination.per_page || 15
        }));
      } else {
        setPagination(prev => ({
          ...prev,
          totalPages: 1,
          totalItems: productsData.length
        }));
      }
      
      // Calculate stats
      const totalValue = productsData.reduce((sum, product) => {
        const price = parseFloat(product.selling_price_pcs || product.selling_price_ctn || 0);
        const stock = parseFloat(product.quantity || 0);
        return sum + (price * stock);
      }, 0);
      
      const lowStock = productsData.filter(product => {
        const stock = parseFloat(product.quantity || 0);
        const reorderLevel = parseInt(product.reorder_level || 5);
        return stock <= reorderLevel && stock > 0;
      }).length;
      
      const outOfStock = productsData.filter(product => {
        const stock = parseFloat(product.quantity || 0);
        return stock <= 0;
      }).length;
      
      setProductStats({
        total_products: productsData.length,
        total_value: totalValue,
        low_stock: lowStock,
        out_of_stock: outOfStock
      });
      
      // Categories are now fetched separately via fetchCategories()
      
    } catch (error) {
      console.error('Error fetching products:', error);
      console.error('Error details:', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      
      // Try fallback to public endpoint
      try {
        const fallbackResponse = await apiService.getAllProducts();
        
        const fallbackData = fallbackResponse.products || fallbackResponse.data || fallbackResponse;
        setProducts(Array.isArray(fallbackData) ? fallbackData : []);
        
        // Calculate stats from fallback data
        const totalValue = (Array.isArray(fallbackData) ? fallbackData : []).reduce((sum, product) => {
          const price = parseFloat(product.selling_price_pcs || product.selling_price_ctn || 0);
          const stock = parseFloat(product.quantity || 0);
          return sum + (price * stock);
        }, 0);
        
        setProductStats({
          total_products: Array.isArray(fallbackData) ? fallbackData.length : 0,
          total_value: totalValue,
          low_stock: 0,
          out_of_stock: 0
        });
        
        // Extract categories
        const uniqueCategories = [...new Set((Array.isArray(fallbackData) ? fallbackData : []).map(product => product.category?.name).filter(Boolean))];
        setCategories(uniqueCategories);
        
        toast.success('Products loaded successfully!');
        
      } catch (fallbackError) {
        console.error('Fallback also failed:', fallbackError);
        toast.error(`Failed to fetch products: ${error.message}`);
        
        // Final fallback to empty state
        setProducts([]);
        setCategories([]);
        setProductStats({
          total_products: 0,
          total_value: 0,
          low_stock: 0,
          out_of_stock: 0
        });
      }
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.currentPage, pagination.itemsPerPage]);

  // Debounced search effect
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      fetchProducts();
    }, 500); // 500ms delay for search

    return () => clearTimeout(timeoutId);
  }, [filters.search, fetchProducts]);

  // Immediate effect for non-search filters
  useEffect(() => {
    fetchProducts();
  }, [filters.category, filters.status, filters.sort_by, filters.sort_order, fetchProducts]);

  // Add Product Modal handlers
  const handleAddProductFormChange = useCallback((field, value) => {
    // Transform product name to uppercase
    if (field === 'name') {
      value = value.toUpperCase();
    }
    
    setAddProductForm(prev => ({ ...prev, [field]: value }));
    
    // Clear error when user starts typing
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
    
    // Clear validation error when user starts typing
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: '' }));
    }
    
  }, [formErrors, validationErrors]);

  // Local product name validation against existing products
  const validateProductName = useCallback(async (productName) => {
    if (!productName || productName.trim().length < 2) {
      setValidationErrors(prev => ({ ...prev, name: '' }));
      return;
    }

    setIsValidatingName(true);
    
    try {
      const trimmedName = productName.trim().toUpperCase();
      
      // If we're in edit mode and the name is the same as the current product, allow it
      if (isEditMode && selectedProduct && selectedProduct.name && selectedProduct.name.toUpperCase() === trimmedName) {
        setValidationErrors(prev => ({ ...prev, name: '' }));
        setIsValidatingName(false);
        return;
      }
      
      // First check against current products list (excluding current product if editing)
      const nameExistsInCurrent = products.some(product => {
        // Skip the current product if we're editing
        if (isEditMode && selectedProduct && product.id === selectedProduct.id) {
          return false;
        }
        return product.name && product.name.toUpperCase() === trimmedName;
      });
      
      if (nameExistsInCurrent) {
        setValidationErrors(prev => ({ ...prev, name: 'Product name already exists in database' }));
        setIsValidatingName(false);
        return;
      }
      
      // If not found in current list, try to fetch all products to check comprehensively
      // This is a fallback for when we're on a paginated view
      try {
        const allProductsResponse = await apiService.getProducts({ per_page: 1000 });
        const allProducts = allProductsResponse.data || allProductsResponse;
        
        const nameExistsInAll = allProducts.some(product => {
          // Skip the current product if we're editing
          if (isEditMode && selectedProduct && product.id === selectedProduct.id) {
            return false;
          }
          return product.name && product.name.toUpperCase() === trimmedName;
        });
        
        if (nameExistsInAll) {
          setValidationErrors(prev => ({ ...prev, name: 'Product name already exists in database' }));
        } else {
          setValidationErrors(prev => ({ ...prev, name: '' }));
        }
      } catch (apiError) {
        // If API call fails, just use the current products list validation
        setValidationErrors(prev => ({ ...prev, name: '' }));
      }
      
    } catch (error) {
      console.error('Error validating product name:', error);
      setValidationErrors(prev => ({ ...prev, name: '' }));
    } finally {
      setIsValidatingName(false);
    }
  }, [products, isEditMode, selectedProduct]);

  // Debounce the validation
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      if (addProductForm.name && addProductForm.name.trim().length >= 2) {
        validateProductName(addProductForm.name);
      }
    }, 500); // 500ms delay

    return () => clearTimeout(timeoutId);
  }, [addProductForm.name, validateProductName]);

  const compressImage = (file, maxWidth = 600, quality = 0.6) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();
      
      img.onload = () => {
        try {
          // Calculate new dimensions
          let { width, height } = img;
          if (width > maxWidth) {
            height = (height * maxWidth) / width;
            width = maxWidth;
          }
          
          canvas.width = width;
          canvas.height = height;
          
          // Draw and compress
          ctx.drawImage(img, 0, 0, width, height);
          const compressedDataUrl = canvas.toDataURL('image/jpeg', quality);
          
          // Clean up the blob URL
          URL.revokeObjectURL(img.src);
          
          resolve(compressedDataUrl);
        } catch (error) {
          // Clean up the blob URL on error
          URL.revokeObjectURL(img.src);
          reject(error);
        }
      };
      
      img.onerror = () => {
        // Clean up the blob URL on error
        URL.revokeObjectURL(img.src);
        reject(new Error('Failed to load image'));
      };
      
      img.src = URL.createObjectURL(file);
    });
  };

  const handleImageUpload = useCallback(async (event) => {
    const file = event.target.files[0];
    if (file) {
      // Validate file type
      if (!file.type.startsWith('image/')) {
        toast.error('Please select a valid image file');
        return;
      }
      
      // Validate file size (max 20MB)
      if (file.size > 20 * 1024 * 1024) {
        toast.error('Image size should be less than 20MB');
        return;
      }
      
      try {
        // Compress the image
        const compressedImageUrl = await compressImage(file);
        
        setAddProductForm(prev => ({
          ...prev,
          image_file: file,
          image_preview: compressedImageUrl
        }));
        
        toast.success('Image compressed and ready for upload');
      } catch (error) {
        console.error('Error compressing image:', error);
        toast.error('Error processing image');
      }
    }
  }, []);

  const initializeProductIdSequence = useCallback(() => {
    // Initialize sequence based on existing products
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const monthKey = `${year}${month}`;
    
    // Find the highest sequence number for this month from existing products
    let maxSequence = 0;
    products.forEach(product => {
      if (product.productID && product.productID.startsWith(`PROD-${year}${month}`)) {
        const sequencePart = product.productID.slice(-3);
        const sequenceNum = parseInt(sequencePart, 10);
        if (!isNaN(sequenceNum) && sequenceNum > maxSequence) {
          maxSequence = sequenceNum;
        }
      }
    });
    
    // Set the sequence to the next number
    setProductIdSequence(prev => ({
      ...prev,
      [monthKey]: maxSequence
    }));
  }, [products]);

  const generateProductID = useCallback(() => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const monthKey = `${year}${month}`;
    
    // Find the highest sequence number for this month from existing products
    let maxSequence = 0;
    products.forEach(product => {
      if (product.productID && product.productID.startsWith(`PROD-${year}${month}`)) {
        const sequencePart = product.productID.slice(-3);
        const sequenceNum = parseInt(sequencePart, 10);
        if (!isNaN(sequenceNum) && sequenceNum > maxSequence) {
          maxSequence = sequenceNum;
        }
      }
    });
    
    // Get current sequence for this month and use the higher of the two
    const currentSequence = productIdSequence[monthKey] || 0;
    const nextSequence = Math.max(currentSequence, maxSequence) + 1;
    
    // Update the sequence for this month
    setProductIdSequence(prev => ({
      ...prev,
      [monthKey]: nextSequence
    }));
    
    // Format the sequence number with leading zeros
    const sequenceNum = String(nextSequence).padStart(3, '0');
    
    return `PROD-${year}${month}${sequenceNum}`;
  }, [productIdSequence, products]);

  // Smart Pricing Calculations
  const calculatePricing = useCallback(() => {
    const costPrice = parseFloat(addProductForm.cost_price) || 0;
    const piecesInContainer = parseFloat(addProductForm.pieces_in_ctn) || 1;
    const vatRate = parseFloat(addProductForm.vat_rate) || 0;
    const standardMargin = parseFloat(addProductForm.standard_margin) || 0;
    const currentMargin = parseFloat(addProductForm.current_margin) || 0;
    const vatType = addProductForm.vat_type;
    const uom = addProductForm.unit_of_measure;

    // Calculate cost per piece based on UoM
    let costPerPiece;
    if (uom === 'ctn' || uom === 'bx' || uom === 'pck') {
      // For containers, cost price is for the whole container, so divide by pieces
      costPerPiece = costPrice / piecesInContainer;
    } else {
      // For pieces, grams, liters - cost price is already per unit
      costPerPiece = costPrice;
    }

    // Calculate unit cost with and without VAT (per piece)
    let unitCostWithoutVAT = costPerPiece;
    let unitCostWithVAT = costPerPiece;

    if (vatType === 'inclusive') {
      // Cost price includes VAT, so extract VAT
      unitCostWithoutVAT = costPerPiece / (1 + vatRate / 100);
      unitCostWithVAT = costPerPiece;
    } else if (vatType === 'exclusive') {
      // Cost price excludes VAT, so add VAT
      unitCostWithoutVAT = costPerPiece;
      unitCostWithVAT = costPerPiece * (1 + vatRate / 100);
    } else {
      // Zero rated - no VAT
      unitCostWithoutVAT = costPerPiece;
      unitCostWithVAT = costPerPiece;
    }

    // Calculate selling prices based on current margin
    const marginMultiplier = 1 + (currentMargin / 100);
    
    // Unit selling price (per piece)
    let unitSellingPrice = unitCostWithVAT * marginMultiplier;
    
    // Container selling price (unit price × pieces in container)
    let containerSellingPrice = unitSellingPrice * piecesInContainer;

    // Apply VAT to selling prices if needed
    let sellingPricePcs = unitSellingPrice;
    let sellingPriceCtn = containerSellingPrice;

    if (vatType === 'exclusive') {
      // Add VAT to selling prices
      sellingPricePcs = unitSellingPrice * (1 + vatRate / 100);
      sellingPriceCtn = containerSellingPrice * (1 + vatRate / 100);
    }

    return {
      unitCostWithoutVAT: unitCostWithoutVAT.toFixed(2),
      unitCostWithVAT: unitCostWithVAT.toFixed(2),
      sellingPricePcs: sellingPricePcs.toFixed(2),
      sellingPriceCtn: sellingPriceCtn.toFixed(2),
      calculatedMargin: currentMargin,
      costPerPiece: costPerPiece.toFixed(2)
    };
  }, [addProductForm.cost_price, addProductForm.pieces_in_ctn, addProductForm.vat_rate, addProductForm.vat_type, addProductForm.standard_margin, addProductForm.current_margin, addProductForm.unit_of_measure]);

  // Auto-calculate pricing when relevant fields change
  useEffect(() => {
    if (addProductForm.cost_price && addProductForm.current_margin) {
      const pricing = calculatePricing();
      
      // Update form with calculated values
      setAddProductForm(prev => ({
        ...prev,
        selling_price_pcs: pricing.sellingPricePcs,
        selling_price_ctn: pricing.sellingPriceCtn
      }));
    }
  }, [addProductForm.cost_price, addProductForm.pieces_in_ctn, addProductForm.vat_rate, addProductForm.vat_type, addProductForm.current_margin, calculatePricing]);

  const validateAddProductForm = useCallback(() => {
    const errors = {};
    
    if (!addProductForm.name.trim()) {
      errors.name = 'Product name is required';
    }
    // Only require Product ID for new products, not when editing
    if (!isEditMode && !addProductForm.productID.trim()) {
      errors.productID = 'Product ID is required';
    }
    if (!addProductForm.category_id) {
      errors.category_id = 'Category is required';
    }
    if (!addProductForm.cost_price || parseFloat(addProductForm.cost_price) < 0) {
      errors.cost_price = 'Valid cost price is required';
    }
    if (!addProductForm.selling_price_pcs || parseFloat(addProductForm.selling_price_pcs) < 0) {
      errors.selling_price_pcs = 'Valid selling price is required';
    }
    if (!addProductForm.quantity || parseFloat(addProductForm.quantity) < 0) {
      errors.quantity = 'Valid quantity is required';
    }
    if (!addProductForm.reorder_level || parseInt(addProductForm.reorder_level) < 0) {
      errors.reorder_level = 'Valid reorder level is required';
    }
    
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [addProductForm, isEditMode]);

  const handleAddProduct = useCallback(async (e) => {
    e.preventDefault();
    
    // Check for validation errors
    if (validationErrors.name) {
      toast.error('Please fix the product name validation error before submitting');
      return;
    }
    
    // Check if name is still being validated
    if (isValidatingName) {
      toast.error('Please wait for product name validation to complete');
      return;
    }
    
    if (!validateAddProductForm()) {
      return;
    }
    
    setAddProductLoading(true);
    try {
      const productData = {
        name: addProductForm.name,
        productID: addProductForm.productID,
        description: addProductForm.description || '',
        category_id: addProductForm.category_id || null,
        supplier_id: addProductForm.supplier_id || null,
        // Map to expected validation fields
        price: parseFloat(addProductForm.selling_price_pcs) || 0,
        stock: parseFloat(addProductForm.quantity) || 0,
        // Additional fields that might be needed
        cost_price: parseFloat(addProductForm.cost_price) || 0,
        selling_price_pcs: parseFloat(addProductForm.selling_price_pcs) || 0,
        selling_price_ctn: addProductForm.selling_price_ctn ? parseFloat(addProductForm.selling_price_ctn) : null,
        quantity: parseFloat(addProductForm.quantity) || 0,
        reorder_level: parseInt(addProductForm.reorder_level) || 0,
        unit_of_measure: addProductForm.unit_of_measure,
        pieces_in_ctn: addProductForm.pieces_in_ctn ? parseInt(addProductForm.pieces_in_ctn) : null,
        vat_type: addProductForm.vat_type,
        vat_rate: parseFloat(addProductForm.vat_rate) || 0,
        standard_margin: addProductForm.standard_margin ? parseFloat(addProductForm.standard_margin) : null,
        current_margin: addProductForm.current_margin ? parseFloat(addProductForm.current_margin) : null
      };
      
      
      // Add image URL if image file is present (use compressed version)
      if (addProductForm.image_file && addProductForm.image_preview) {
        // Use the already compressed image preview
        productData.image_url = addProductForm.image_preview;
        
      }
      
      // Send the product data
      if (isEditMode && selectedProduct) {
        await apiService.updateProduct(selectedProduct.id, productData);
        toast.success('Product updated successfully!');
      } else {
        await apiService.createProduct(productData);
        toast.success('Product added successfully!');
      }
      setShowAddModal(false);
      setAddProductForm({
        name: '',
        productID: '',
        description: '',
        image_file: null,
        image_preview: '',
        category_id: '',
        supplier_id: '',
        cost_price: '',
        selling_price_pcs: '',
        selling_price_ctn: '',
        quantity: '',
        reorder_level: '',
        unit_of_measure: 'pcs',
        pieces_in_ctn: '',
        vat_type: 'inclusive',
        vat_rate: '0',
        standard_margin: '',
        current_margin: ''
      });
      setFormErrors({});
      setValidationErrors({});
      setIsValidatingName(false);
      
      // Refresh the products list
      setRefreshTrigger(prev => prev + 1);
      
    } catch (error) {
      console.error('Error adding product:', error);
      
      // Enhanced error handling for different error types
      let errorMessage = 'Failed to add product. Please try again.';
      let validationErrors = {};
      
      if (error.response) {
        const { status, data } = error.response;
        
        if (status === 422 && data && data.errors) {
          // Laravel validation errors - map to form fields
          const serverErrors = data.errors;
          const mappedErrors = {};
          
          // Map server validation errors to form fields
          Object.keys(serverErrors).forEach(field => {
            const fieldErrors = serverErrors[field];
            if (Array.isArray(fieldErrors) && fieldErrors.length > 0) {
              mappedErrors[field] = fieldErrors[0];
            }
          });
          
          setFormErrors(mappedErrors);
          errorMessage = 'Please fix the validation errors below.';
          
        } else if (status === 500) {
          // Server error - try to get more details
          if (data && data.message) {
            errorMessage = `Server error: ${data.message}`;
          } else if (data && data.error) {
            errorMessage = `Server error: ${data.error}`;
          } else {
            errorMessage = 'Server error occurred. Please check your data and try again.';
          }
          
        } else if (status === 404) {
          errorMessage = 'Product not found. Please refresh and try again.';
          
        } else if (status === 403) {
          errorMessage = 'You do not have permission to perform this action.';
          
        } else if (status === 401) {
          errorMessage = 'Authentication required. Please log in again.';
          
        } else if (data && data.message) {
          errorMessage = data.message;
        }
        
      } else if (error.message) {
        // Network or other errors
        if (error.message.includes('Network Error')) {
          errorMessage = 'Network error. Please check your connection and try again.';
        } else if (error.message.includes('timeout')) {
          errorMessage = 'Request timeout. Please try again.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast.error(errorMessage);
    } finally {
      setAddProductLoading(false);
    }
  }, [addProductForm, validateAddProductForm, isEditMode, selectedProduct]);

  const handleCloseAddModal = useCallback(() => {
    setShowAddModal(false);
    setIsEditMode(false);
    setSelectedProduct(null);
    // Clean up image preview URL if it exists and is a blob URL
    if (addProductForm.image_preview && addProductForm.image_preview.startsWith('blob:')) {
      URL.revokeObjectURL(addProductForm.image_preview);
    }
    setAddProductForm({
      name: '',
      productID: '',
      description: '',
      image_file: null,
      image_preview: '',
      category_id: '',
      supplier_id: '',
      cost_price: '',
      selling_price_pcs: '',
      selling_price_ctn: '',
      quantity: '',
      reorder_level: '',
      unit_of_measure: 'pcs',
      pieces_in_ctn: '',
      vat_type: 'inclusive',
      vat_rate: '0',
      standard_margin: '',
      current_margin: ''
    });
    setFormErrors({});
    setValidationErrors({});
    setIsValidatingName(false);
  }, [addProductForm.image_preview]);

  // Image Preview handlers
  const handleImageClick = useCallback((product) => {
    if (product.image_url) {
      setSelectedImage({
        url: product.image_url,
        name: product.name,
        productID: product.productID || `PROD-${product.id}`
      });
      setShowImageModal(true);
    }
  }, []);

  const handleCloseImageModal = useCallback(() => {
    setShowImageModal(false);
    setSelectedImage(null);
  }, []);

  // Product action handlers
  const handleViewProduct = useCallback((product) => {
    setSelectedProduct(product);
    setShowViewModal(true);
  }, []);

  const handleEditProduct = useCallback((product) => {
    setSelectedProduct(product);
    setIsEditMode(true);
    setAddProductForm({
      name: product.name || '',
      productID: product.productID || '',
      description: product.description || '',
      category_id: product.category_id || '',
      supplier_id: product.supplier_id || '',
      cost_price: product.cost_price || '',
      selling_price_pcs: product.selling_price_pcs || '',
      selling_price_ctn: product.selling_price_ctn || '',
      quantity: product.quantity || '',
      reorder_level: product.reorder_level || '',
      unit_of_measure: product.unit_of_measure || 'pcs',
      pieces_in_ctn: product.pieces_in_ctn || '',
      vat_type: product.vat_type || 'inclusive',
      vat_rate: product.vat_rate || '0',
      standard_margin: product.standard_margin || '',
      current_margin: product.current_margin || '',
      image_url: product.image_url || '',
      image_file: null,
      image_preview: product.image_url || ''
    });
    setShowAddModal(true);
  }, []);

  const handleDeleteProduct = useCallback((product) => {
    if (!product || !product.id) {
      toast.error('Invalid product selected for deletion');
      return;
    }
    setSelectedProduct(product);
    setShowDeleteModal(true);
  }, []);

  const handleCloseViewModal = useCallback(() => {
    setShowViewModal(false);
    setSelectedProduct(null);
  }, []);


  const handleCloseDeleteModal = useCallback(() => {
    setShowDeleteModal(false);
    setSelectedProduct(null);
  }, []);


  // Delete product handler
  const handleConfirmDelete = useCallback(async () => {
    if (!selectedProduct) return;
    
    setDeleteProductLoading(true);
    try {
      const response = await apiService.deleteProduct(selectedProduct.id);
      
      if (response.success) {
        toast.success('Product deleted successfully!');
        setShowDeleteModal(false);
        setSelectedProduct(null);
        
        // Refresh the products list
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error(response.message || 'Failed to delete product');
      }
    } catch (error) {
      console.error('Error deleting product:', error);
      
      // Handle specific error cases
      if (error.message.includes('404')) {
        toast.error('Product not found. It may have already been deleted.');
        // Refresh the list to remove the deleted product
        setRefreshTrigger(prev => prev + 1);
      } else {
        toast.error('Failed to delete product: ' + error.message);
      }
    } finally {
      setDeleteProductLoading(false);
    }
  }, [selectedProduct]);

  // Create a refresh trigger state
  const [refreshTrigger, setRefreshTrigger] = useState(0);

  // Fetch categories for dropdown
  const fetchCategories = useCallback(async () => {
    try {
      const response = await apiService.getAllCategories();
      
      const categoriesData = response.data || response;
      setCategories(Array.isArray(categoriesData) ? categoriesData : []);
    } catch (error) {
      console.error('Error fetching categories:', error);
      // Try fallback to public endpoint
      try {
        const fallbackResponse = await apiService.getAllCategoriesPublic();
        const fallbackData = fallbackResponse.data || fallbackResponse;
        setCategories(Array.isArray(fallbackData) ? fallbackData : []);
      } catch (fallbackError) {
        console.error('Fallback categories also failed:', fallbackError);
        setCategories([]);
      }
    }
  }, []);

  // Fetch suppliers for dropdown
  const fetchSuppliers = useCallback(async () => {
    try {
      const response = await apiService.getAllSuppliers();
      
      const suppliersData = response.data || response;
      setSuppliers(Array.isArray(suppliersData) ? suppliersData : []);
    } catch (error) {
      console.error('Error fetching suppliers:', error);
      // Fallback: Create some default suppliers if the endpoint doesn't exist
      const defaultSuppliers = [
        { id: 1, name: 'Default Supplier', supplier_name: 'Default Supplier' },
        { id: 2, name: 'Local Vendor', supplier_name: 'Local Vendor' },
        { id: 3, name: 'Wholesale Distributor', supplier_name: 'Wholesale Distributor' }
      ];
      setSuppliers(defaultSuppliers);
    }
  }, []);

  // Update active dropdown when location changes
  useEffect(() => {
    if (location.pathname.startsWith('/customer')) {
      setActiveDropdown(3); // Customers submenu index
    } else if (location.pathname.startsWith('/product')) {
      setActiveDropdown(2); // Products submenu index
    } else if (location.pathname.startsWith('/pos') || location.pathname.startsWith('/retail') || location.pathname.startsWith('/bulk') || location.pathname.startsWith('/proforma') || location.pathname.startsWith('/credit')) {
      setActiveDropdown(1); // POS Operations submenu index
    } else if (location.pathname.startsWith('/report')) {
      setActiveDropdown(4); // Reports submenu index
    } else if (location.pathname.startsWith('/setting') || location.pathname.startsWith('/user')) {
      setActiveDropdown(5); // Settings submenu index
    } else {
      setActiveDropdown(null);
    }
  }, [location.pathname]);

  useEffect(() => {
    fetchCategories();
    fetchSuppliers();
  }, [fetchCategories, fetchSuppliers]);

  // Initialize product ID sequence when products are loaded
  useEffect(() => {
    if (products.length > 0) {
      initializeProductIdSequence();
    }
  }, [products, initializeProductIdSequence]);

  // Trigger refresh when refreshTrigger changes
  useEffect(() => {
    if (refreshTrigger > 0) {
      fetchProducts();
    }
  }, [refreshTrigger, fetchProducts]);

  // Set active dropdown based on current location
  useEffect(() => {
    const activeIndex = navigationItems.findIndex(item => 
      item.children && item.children.some(child => child.active)
    );
    setActiveDropdown(activeIndex >= 0 ? activeIndex : null);
  }, [location.pathname, navigationItems]);

  // Initial load of products
  useEffect(() => {
    fetchProducts();
  }, []); // Empty dependency array means this runs once on mount

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (userMenuOpen && !event.target.closest('.user-menu-container')) {
        setUserMenuOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [userMenuOpen]);

  // Add error boundary check
  if (!user) {
    return <div className="min-h-screen bg-gray-50 flex items-center justify-center">
      <div className="text-center">
        <h2 className="text-2xl font-bold text-gray-900 mb-4">Loading...</h2>
        <p className="text-gray-600">Please wait while we load your products.</p>
      </div>
    </div>;
  }


  return (
    <Layout>
      <main className="flex-1 p-2 sm:p-4 lg:p-6 overflow-y-auto pt-20">
        <div className="w-full space-y-6 sm:space-y-8 lg:space-y-10">
          {/* Page Header */}
          <div className="flex flex-col mt-10 sm:flex-row sm:items-center justify-between gap-4">
            <div>
              <h1 className="text-2xl sm:text-3xl font-bold text-gray-900">Product Management</h1>
              <p className="text-sm sm:text-base text-gray-600">Manage your product inventory and catalog</p>
            </div>
            <div className="flex items-center gap-2 sm:gap-3">
              <button 
                onClick={() => {
                  // Reset form first to ensure clean state
                  setAddProductForm({
                    name: '',
                    productID: '',
                    description: '',
                    image_file: null,
                    image_preview: '',
                    category_id: '',
                    supplier_id: '',
                    cost_price: '',
                    selling_price_pcs: '',
                    selling_price_ctn: '',
                    quantity: '',
                    reorder_level: '',
                    unit_of_measure: 'pcs',
                    pieces_in_ctn: '',
                    vat_type: 'inclusive',
                    vat_rate: '0',
                    standard_margin: '',
                    current_margin: ''
                  });
                  
                  // Generate new product ID
                  const newProductID = generateProductID();
                  
                  // Set the new product ID
                  setAddProductForm(prev => ({
                    ...prev,
                    productID: newProductID
                  }));
                  
                  // Clear any form errors
                  setFormErrors({});
                  setValidationErrors({});
                  
                  // Open modal
                  setShowAddModal(true);
                }}
                className="px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-red-500 to-blue-600 text-white rounded-lg hover:from-red-600 hover:to-blue-700 transition-all duration-300 flex items-center gap-2 text-sm sm:text-base shadow-lg hover:shadow-xl"
              >
                <Plus size={16} className="sm:w-5 sm:h-5" />
                <span className="hidden sm:inline">Add Product</span>
                <span className="sm:hidden">Add</span>
              </button>
              <button 
                onClick={fetchProducts}
                disabled={loading}
                className="px-3 py-2 sm:px-4 sm:py-3 bg-gradient-to-r from-emerald-500 to-teal-600 text-white rounded-lg hover:from-emerald-600 hover:to-teal-700 transition-all duration-300 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed text-sm sm:text-base shadow-lg hover:shadow-xl"
              >
                <RefreshCw size={16} className={`${loading ? 'animate-spin' : ''} sm:w-5 sm:h-5`} />
                <span className="hidden sm:inline">Refresh</span>
              </button>
            </div>
          </div>

          {/* Search and Filter Section */}
          <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search Bar */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                  <input
                    type="text"
                    placeholder="Search products by name or ID..."
                    value={filters.search}
                    onChange={(e) => handleFilterChange('search', e.target.value)}
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  />
                </div>
              </div>

              {/* Filter Controls */}
              <div className="flex flex-col sm:flex-row gap-3">
                {/* Category Filter */}
                <div className="min-w-0 sm:w-48">
                  <select
                    value={filters.category}
                    onChange={(e) => handleFilterChange('category', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="all">All Categories</option>
                    {categories.map((category) => (
                      <option key={category.id} value={category.id}>
                        {category.main_cat}
                      </option>
                    ))}
                  </select>
                </div>

                {/* Status Filter */}
                <div className="min-w-0 sm:w-32">
                  <select
                    value={filters.status}
                    onChange={(e) => handleFilterChange('status', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="in_stock">In Stock</option>
                    <option value="low_stock">Low Stock</option>
                    <option value="out_of_stock">Out of Stock</option>
                  </select>
                </div>

                {/* Sort By */}
                <div className="min-w-0 sm:w-32">
                  <select
                    value={filters.sort_by}
                    onChange={(e) => handleFilterChange('sort_by', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="name">Name</option>
                    <option value="created_at">Date Added</option>
                    <option value="cost_price">Cost Price</option>
                    <option value="selling_price_pcs">Unit Price</option>
                    <option value="quantity">Stock</option>
                  </select>
                </div>

                {/* Sort Order */}
                <div className="min-w-0 sm:w-24">
                  <select
                    value={filters.sort_order}
                    onChange={(e) => handleFilterChange('sort_order', e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm"
                  >
                    <option value="asc">Asc</option>
                    <option value="desc">Desc</option>
                  </select>
                </div>

                {/* Clear Filters */}
                <button
                  onClick={() => setFilters({
                    category: 'all',
                    status: 'all',
                    search: '',
                    sort_by: 'name',
                    sort_order: 'asc'
                  })}
                  className="px-4 py-2 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white rounded-lg transition-all duration-200 text-sm font-medium flex items-center gap-2 shadow-md hover:shadow-lg transform hover:scale-105 active:scale-95"
                >
                  <X size={16} className="flex-shrink-0" />
                  <span className="hidden sm:inline">Clear Filters</span>
                  <span className="sm:hidden">Clear</span>
                </button>
              </div>
            </div>

            {/* Active Filters Display */}
            {(filters.search || filters.category !== 'all' || filters.status !== 'all') && (
              <div className="mt-4 pt-4 border-t border-gray-200">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm text-gray-600">Active filters:</span>
                  {filters.search && (
                    <span className="inline-flex items-center px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full">
                      Search: "{filters.search}"
                      <button
                        onClick={() => handleFilterChange('search', '')}
                        className="ml-1 hover:text-blue-600"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.category !== 'all' && (
                    <span className="inline-flex items-center px-2 py-1 bg-green-100 text-green-800 text-xs rounded-full">
                      Category: {categories.find(c => c.id == filters.category)?.main_cat || 'Unknown'}
                      <button
                        onClick={() => handleFilterChange('category', 'all')}
                        className="ml-1 hover:text-green-600"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  )}
                  {filters.status !== 'all' && (
                    <span className="inline-flex items-center px-2 py-1 bg-orange-100 text-orange-800 text-xs rounded-full">
                      Status: {filters.status.replace('_', ' ')}
                      <button
                        onClick={() => handleFilterChange('status', 'all')}
                        className="ml-1 hover:text-orange-600"
                      >
                        <X size={12} />
                      </button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>

              {/* Products Table - Desktop View */}
              <div className="hidden lg:block bg-white rounded-lg border border-gray-200 overflow-hidden">
                <div className="overflow-x-auto">
                  <table className="w-full min-w-[1000px]">
                    <thead className="bg-gray-50 border-b border-gray-200">
                      <tr>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48">
                          Product
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-32">
                          Category
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                          Cost
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                          Unit
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                          Carton
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                          Stock
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-20">
                          Status
                        </th>
                        <th className="px-3 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-24">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {loading ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center">
                            <div className="flex items-center justify-center">
                              <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
                              <span className="ml-2 text-gray-600">Loading products...</span>
                            </div>
                          </td>
                        </tr>
                      ) : products.length === 0 ? (
                        <tr>
                          <td colSpan="8" className="px-6 py-12 text-center">
                            <div className="flex flex-col items-center">
                              <Package className="h-12 w-12 text-gray-400 mb-4" />
                              <p className="text-gray-600 text-lg font-medium">No products found</p>
                              <p className="text-gray-500 text-sm">Add your first product to get started</p>
                            </div>
                          </td>
                        </tr>
                      ) : (
                        products.map((product) => {
                          const stock = parseFloat(product.quantity || 0);
                          const reorderLevel = parseInt(product.reorder_level || 5);
                          const isLowStock = stock <= reorderLevel && stock > 0;
                          const isOutOfStock = stock <= 0;
                          
                          return (
                            <tr key={product.id} className="hover:bg-gray-50 transition-colors duration-200">
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div 
                                    className="w-10 h-10 rounded-lg overflow-hidden mr-2 flex-shrink-0 group cursor-pointer"
                                    onClick={() => handleImageClick(product)}
                                  >
                                    {product.image_url ? (
                                      <img 
                                        src={product.image_url} 
                                        alt={product.name}
                                        className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                                        onError={(e) => {
                                          console.warn('Image failed to load:', product.image_url);
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                        onLoad={(e) => {
                                          // Image loaded successfully
                                          e.target.style.display = 'block';
                                          e.target.nextSibling.style.display = 'none';
                                        }}
                                      />
                                    ) : null}
                                    <div 
                                      className={`w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center group-hover:from-blue-200 group-hover:to-blue-300 transition-colors duration-200 ${product.image_url ? 'hidden' : 'flex'}`}
                                    >
                                      <Package size={16} className="text-blue-600" />
                                    </div>
                                  </div>
                                  <div className="min-w-0 flex-1">
                                    <div className="text-sm font-semibold text-gray-900 truncate">{product.name}</div>
                                    <div className="text-xs text-gray-500 truncate">
                                      {product.productID || `ID: ${product.id}`}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <div className="flex items-center">
                                  <div className="w-4 h-4 bg-blue-100 rounded flex items-center justify-center mr-1">
                                    <Tag size={8} className="text-blue-600" />
                                  </div>
                                  <div>
                                    <div className="text-sm font-semibold text-gray-900 truncate">
                                      {product.category?.main_cat || 'Uncategorized'}
                                    </div>
                                  </div>
                                </div>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                                {formatCurrency(parseFloat(product.cost_price) || 0, true, 'text-red-600 font-semibold')}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                                {formatCurrency(parseFloat(product.selling_price_pcs) || 0, true, 'text-green-600 font-semibold')}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm font-semibold text-gray-900">
                                {formatCurrency(parseFloat(product.selling_price_ctn) || 0, true, 'text-blue-600 font-semibold')}
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <span className={`text-sm font-semibold ${
                                  isOutOfStock ? 'text-red-600' : 
                                  isLowStock ? 'text-orange-600' : 
                                  'text-green-600'
                                }`}>
                                  {formatQuantity(stock)}
                                </span>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap">
                                <span className={`inline-flex px-1 py-0.5 text-sm font-semibold rounded-full ${
                                  isOutOfStock ? 'bg-red-100 text-red-800' :
                                  isLowStock ? 'bg-orange-100 text-orange-800' : 
                                  'bg-green-100 text-green-800'
                                }`}>
                                  {isOutOfStock ? 'Out' : 
                                   isLowStock ? 'Low' : 
                                   'In'}
                                </span>
                              </td>
                              <td className="px-3 py-3 whitespace-nowrap text-sm font-medium">
                                <div className="flex items-center space-x-2">
                                  <button 
                                    onClick={() => handleViewProduct(product)}
                                    className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                    title="View"
                                  >
                                    <Eye size={16} />
                                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                      View
                                    </span>
                                  </button>
                                  <button 
                                    onClick={() => handleEditProduct(product)}
                                    className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                    title="Edit"
                                  >
                                    <Edit size={16} />
                                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                      Edit
                                    </span>
                                  </button>
                                  <button 
                                    onClick={() => handleDeleteProduct(product)}
                                    className="group/btn relative p-2 text-red-600 bg-red-100 hover:bg-red-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                    title="Delete"
                                  >
                                    <Trash2 size={16} />
                                    <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none">
                                      Delete
                                    </span>
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Mobile Card View */}
              <div className="lg:hidden space-y-4">
                {loading ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <div className="flex items-center justify-center">
                      <Loader2 className="animate-spin h-8 w-8 text-blue-600" />
                      <span className="ml-2 text-gray-600">Loading products...</span>
                    </div>
                  </div>
                ) : products.length === 0 ? (
                  <div className="bg-white rounded-lg border border-gray-200 p-8 text-center">
                    <div className="flex flex-col items-center">
                      <Package className="h-12 w-12 text-gray-400 mb-4" />
                      <p className="text-gray-600 text-lg font-medium">No products found</p>
                      <p className="text-gray-500 text-sm">Add your first product to get started</p>
                    </div>
                  </div>
                ) : (
                  products.map((product) => {
                    const stock = parseFloat(product.quantity || 0);
                    const reorderLevel = parseInt(product.reorder_level || 5);
                    const isLowStock = stock <= reorderLevel && stock > 0;
                    const isOutOfStock = stock <= 0;
                    
                    return (
                      <div key={product.id} className="bg-white rounded-lg border border-gray-200 p-4">
                        <div className="flex items-start space-x-3">
                          {/* Product Image */}
                          <div 
                            className="w-16 h-16 rounded-lg overflow-hidden flex-shrink-0 group cursor-pointer"
                            onClick={() => handleImageClick(product)}
                          >
                            {product.image_url ? (
                              <img 
                                src={product.image_url} 
                                alt={product.name}
                                className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-200"
                                onError={(e) => {
                                  console.warn('Image failed to load:', product.image_url);
                                  e.target.style.display = 'none';
                                  e.target.nextSibling.style.display = 'flex';
                                }}
                                onLoad={(e) => {
                                  // Image loaded successfully
                                  e.target.style.display = 'block';
                                  e.target.nextSibling.style.display = 'none';
                                }}
                              />
                            ) : null}
                            <div 
                              className={`w-full h-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center group-hover:from-blue-200 group-hover:to-blue-300 transition-colors duration-200 ${product.image_url ? 'hidden' : 'flex'}`}
                            >
                              <Package size={24} className="text-blue-600" />
                            </div>
                          </div>
                          
                          {/* Product Details */}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-start justify-between">
                              <div className="flex-1 min-w-0">
                                <h3 className="text-sm font-semibold text-gray-900 truncate">{product.name}</h3>
                                <p className="text-xs text-gray-500 truncate">
                                  {product.productID || `ID: ${product.id}`}
                                </p>
                                <div className="flex items-center mt-1">
                                  <div className="w-4 h-4 bg-blue-100 rounded flex items-center justify-center mr-1">
                                    <Tag size={10} className="text-blue-600" />
                                  </div>
                                  <span className="text-xs text-gray-600">
                                    {product.category?.main_cat || 'Uncategorized'}
                                  </span>
                                </div>
                              </div>
                              
                              {/* Status Badge */}
                              <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ml-2 ${
                                isOutOfStock ? 'bg-red-100 text-red-800' :
                                isLowStock ? 'bg-orange-100 text-orange-800' : 
                                'bg-green-100 text-green-800'
                              }`}>
                                {isOutOfStock ? 'Out' : 
                                 isLowStock ? 'Low' : 
                                 'In Stock'}
                              </span>
                            </div>
                            
                            {/* Price and Stock Info */}
                            <div className="mt-3 grid grid-cols-2 gap-2 text-xs">
                              <div>
                                <span className="text-gray-500">Unit Price:</span>
                                <span className="font-semibold text-gray-900 ml-1">
                                  ₵{(parseFloat(product.unit_price) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                                </span>
                              </div>
                              <div>
                                <span className="text-gray-500">Stock:</span>
                                <span className="font-semibold text-gray-900 ml-1">
                                  {formatQuantity(stock)} {product.unit_of_measure || 'units'}
                                </span>
                              </div>
                            </div>
                            
                            {/* Actions */}
                            <div className="flex items-center justify-end space-x-2 mt-3 pt-3 border-t border-gray-100">
                              <button 
                                onClick={() => handleViewProduct(product)}
                                className="group/btn relative p-2 text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                title="View"
                              >
                                <Eye size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                                  View
                                </span>
                              </button>
                              <button 
                                onClick={() => handleEditProduct(product)}
                                className="group/btn relative p-2 text-green-600 bg-green-100 hover:bg-green-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                title="Edit"
                              >
                                <Edit size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                                  Edit
                                </span>
                              </button>
                              <button 
                                onClick={() => handleDeleteProduct(product)}
                                className="group/btn relative p-2 text-red-600 bg-red-100 hover:bg-red-200 rounded-lg transition-all duration-200 shadow-sm hover:shadow-md hover:scale-110" 
                                title="Delete"
                              >
                                <Trash2 size={18} />
                                <span className="absolute -top-8 left-1/2 transform -translate-x-1/2 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover/btn:opacity-100 transition-opacity duration-200 whitespace-nowrap pointer-events-none z-50">
                                  Delete
                                </span>
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              {/* Pagination */}
              {products.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 p-4 sm:p-6">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center text-xs sm:text-sm text-gray-700">
                      <span>Showing</span>
                      <span className="font-medium mx-1">
                        {((pagination.currentPage - 1) * pagination.itemsPerPage) + 1}
                      </span>
                      <span>to</span>
                      <span className="font-medium mx-1">
                        {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)}
                      </span>
                      <span>of</span>
                      <span className="font-medium ml-1">{pagination.totalItems}</span>
                      <span className="ml-1">results</span>
                    </div>
                    
                    <div className="flex items-center justify-center sm:justify-end space-x-1 sm:space-x-2">
                      <button 
                        onClick={() => {
                          if (pagination.currentPage > 1) {
                            setPagination(prev => ({ ...prev, currentPage: prev.currentPage - 1 }));
                          }
                        }}
                        disabled={pagination.currentPage === 1}
                        className="px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        <span className="hidden sm:inline">Previous</span>
                        <span className="sm:hidden">Prev</span>
                      </button>
                      
                      <div className="flex items-center space-x-1">
                        {Array.from({ length: Math.min(5, pagination.totalPages) }, (_, i) => {
                          let pageNum;
                          if (pagination.totalPages <= 5) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage <= 3) {
                            pageNum = i + 1;
                          } else if (pagination.currentPage >= pagination.totalPages - 2) {
                            pageNum = pagination.totalPages - 4 + i;
                          } else {
                            pageNum = pagination.currentPage - 2 + i;
                          }
                          
                          if (pageNum > pagination.totalPages) return null;
                          
                          return (
                            <button
                              key={pageNum}
                              onClick={() => setPagination(prev => ({ ...prev, currentPage: pageNum }))}
                              className={`px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium rounded-lg transition-colors duration-200 ${
                                pageNum === pagination.currentPage
                                  ? 'bg-blue-600 text-white'
                                  : 'text-gray-500 bg-white border border-gray-300 hover:bg-gray-50 hover:text-gray-700'
                              }`}
                            >
                              {pageNum}
                            </button>
                          );
                        })}
                      </div>
                      
                      <button 
                        onClick={() => {
                          if (pagination.currentPage < pagination.totalPages) {
                            setPagination(prev => ({ ...prev, currentPage: prev.currentPage + 1 }));
                          }
                        }}
                        disabled={pagination.currentPage === pagination.totalPages}
                        className="px-2 py-1 sm:px-3 sm:py-2 text-xs sm:text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                      >
                        <span className="hidden sm:inline">Next</span>
                        <span className="sm:hidden">Next</span>
                      </button>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </main>

          {/* Add Product Modal */}
      {showAddModal && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
            {/* Header */}
            <div className="bg-gradient-to-r from-red-500 to-blue-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                    <Plus size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">{isEditMode ? 'Edit Product' : 'Add New Product'}</h2>
                    <p className="text-white text-opacity-80 text-sm">
                      {isEditMode ? 'Update product information' : 'Create a new product in your inventory'}
                    </p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseAddModal}
                    className="px-4 py-2 text-white bg-white bg-opacity-10 hover:bg-opacity-20 backdrop-blur-sm border border-white border-opacity-20 rounded-xl transition-all duration-200 font-medium text-sm hover:scale-105 active:scale-95"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="add-product-form"
                    disabled={addProductLoading}
                    className="px-5 py-2 bg-white text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 border border-blue-100"
                  >
                    {addProductLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        {isEditMode ? 'Updating...' : 'Adding...'}
                      </>
                    ) : (
                      <>
                        <Check size={16} />
                        {isEditMode ? 'Update' : 'Add Product'}
                      </>
                    )}
                  </button>
                  <button
                    onClick={handleCloseAddModal}
                    className="px-3 py-2 text-white bg-red-500 hover:bg-red-600 rounded-xl transition-all duration-200 font-medium text-sm hover:scale-105 active:scale-95 shadow-md hover:shadow-lg"
                  >
                    <X size={16} />
                  </button>
                </div>
              </div>
            </div>

            <form id="add-product-form" onSubmit={handleAddProduct} className="overflow-y-auto max-h-[calc(95vh-100px)]">
              <div className="p-6 space-y-6">
                {/* Basic Information Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-blue-100 rounded-lg flex items-center justify-center">
                      <Package size={14} className="text-blue-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Basic Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
                    {/* Product Name */}
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product Name *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={addProductForm.name}
                          onChange={(e) => handleAddProductFormChange('name', e.target.value)}
                          className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                            formErrors.name || validationErrors.name ? 'border-red-500 bg-red-50' : 
                            isValidatingName ? 'border-yellow-400 bg-yellow-50' : 'border-gray-300 hover:border-gray-400'
                          }`}
                          placeholder="Enter product name (will be converted to uppercase)"
                          style={{ textTransform: 'uppercase' }}
                        />
                        {isValidatingName && (
                          <div className="absolute right-3 top-1/2 transform -translate-y-1/2">
                            <Loader2 size={16} className="animate-spin text-blue-500" />
                          </div>
                        )}
                      </div>
                      {formErrors.name && (
                        <p className="text-red-500 text-sm mt-2 flex items-center">
                          <AlertCircle size={14} className="mr-1" />
                          {formErrors.name}
                        </p>
                      )}
                      {validationErrors.name && (
                        <p className="text-red-500 text-sm mt-2 flex items-center">
                          <AlertCircle size={14} className="mr-1" />
                          {validationErrors.name}
                        </p>
                      )}
                      {addProductForm.name && !validationErrors.name && !isValidatingName && addProductForm.name.trim().length >= 2 && (
                        <p className="text-green-500 text-sm mt-2 flex items-center">
                          <CheckCircle size={14} className="mr-1" />
                          Product name is available
                        </p>
                      )}
                    </div>

                    {/* Product ID */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Product ID / SKU *
                      </label>
                      <div className="relative">
                        <input
                          type="text"
                          value={addProductForm.productID || ''}
                          readOnly
                          className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gradient-to-r from-blue-50 to-indigo-50 text-gray-800 font-mono text-sm font-semibold"
                          placeholder="Auto-generated"
                        />
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
                          <div className="w-2 h-2 bg-green-500 rounded-full animate-pulse"></div>
                        </div>
                      </div>
                      <p className="text-xs text-gray-500 mt-1 flex items-center gap-1">
                        <div className="w-1.5 h-1.5 bg-green-500 rounded-full"></div>
                        Auto-generated Product ID
                      </p>
                    </div>

                    {/* Category */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Category *
                      </label>
                      <select
                        value={addProductForm.category_id}
                        onChange={(e) => handleAddProductFormChange('category_id', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors ${
                          formErrors.category_id ? 'border-red-500 bg-red-50' : 'border-gray-300 hover:border-gray-400'
                        }`}
                      >
                        <option value="">Select Category</option>
                        {categories.map((category) => (
                          <option key={category.id} value={category.id}>
                            {category.main_cat} - {category.sub_cat}
                          </option>
                        ))}
                      </select>
                      {formErrors.category_id && (
                        <p className="text-red-500 text-sm mt-2 flex items-center">
                          <AlertCircle size={14} className="mr-1" />
                          {formErrors.category_id}
                        </p>
                      )}
                    </div>

                    {/* Supplier */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Supplier
                      </label>
                      <select
                        value={addProductForm.supplier_id}
                        onChange={(e) => handleAddProductFormChange('supplier_id', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-colors hover:border-gray-400"
                      >
                        <option value="">Select Supplier</option>
                        {suppliers.map((supplier) => (
                          <option key={supplier.id} value={supplier.id}>
                            {supplier.name || supplier.supplier_name || `Supplier ${supplier.id}`}
                          </option>
                        ))}
                        {suppliers.length === 0 && (
                          <option value="1" disabled>No suppliers available</option>
                        )}
                      </select>
                    </div>
                  </div>
                </div>

                {/* Product Image Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                      <Package2 size={14} className="text-green-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Product Image</h3>
                  </div>
                  
                  <div className="flex items-start space-x-3">
                    {/* Image Preview */}
                    <div className="w-16 h-16 rounded-lg overflow-hidden bg-white border-2 border-gray-200 flex items-center justify-center shadow-sm">
                      {addProductForm.image_preview ? (
                        <img 
                          src={addProductForm.image_preview} 
                          alt="Product preview"
                          className="w-full h-full object-cover"
                          onError={(e) => {
                            console.warn('Image preview failed to load:', addProductForm.image_preview);
                            e.target.style.display = 'none';
                            e.target.nextSibling.style.display = 'flex';
                          }}
                        />
                      ) : null}
                      {!addProductForm.image_preview && (
                        <div className="w-full h-full bg-gradient-to-br from-gray-100 to-gray-200 flex items-center justify-center">
                          <Package size={20} className="text-gray-400" />
                        </div>
                      )}
                    </div>
                    
                    {/* Upload Button */}
                    <div className="flex-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Upload Image
                      </label>
                      <div className="flex items-center space-x-2">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleImageUpload}
                          className="hidden"
                          id="image-upload"
                        />
                        <label
                          htmlFor="image-upload"
                          className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer text-sm font-medium"
                        >
                          Choose File
                        </label>
                        {addProductForm.image_file && (
                          <span className="text-sm text-gray-600">
                            {addProductForm.image_file.name}
                          </span>
                        )}
                      </div>
                      <p className="text-xs text-gray-500 mt-1 flex items-center">
                        <Info size={10} className="mr-1" />
                        Optional: Upload product image (max 20MB)
                      </p>
                    </div>
                  </div>
                </div>

                {/* Purchasing Information Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center">
                      <ShoppingCart size={14} className="text-orange-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Purchasing Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    {/* Unit of Measure */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit of Measure
                      </label>
                      <select
                        value={addProductForm.unit_of_measure}
                        onChange={(e) => handleAddProductFormChange('unit_of_measure', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="pcs">Pieces (pcs)</option>
                        <option value="ctn">Carton (ctn)</option>
                        <option value="grm">Grams (grm)</option>
                        <option value="ltr">Liters (ltr)</option>
                        <option value="bx">Boxes (bx)</option>
                        <option value="pck">Pack (pck)</option>
                      </select>
                      <p className="text-xs text-gray-500 mt-1">
                        This will update the container field below
                      </p>
                    </div>

                    {/* Pieces in Container - Dynamic Label */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        {(() => {
                          const uom = addProductForm.unit_of_measure;
                          switch (uom) {
                            case 'ctn': return 'Pieces in Carton';
                            case 'bx': return 'Pieces in Box';
                            case 'pck': return 'Pieces in Pack';
                            case 'pcs': return 'Pieces per Unit';
                            case 'grm': return 'Grams per Unit';
                            case 'ltr': return 'Liters per Unit';
                            default: return 'Pieces in Container';
                          }
                        })()}
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={addProductForm.pieces_in_ctn}
                        onChange={(e) => handleAddProductFormChange('pieces_in_ctn', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0"
                      />
                      <p className="text-xs text-gray-500 mt-1">
                        {(() => {
                          const uom = addProductForm.unit_of_measure;
                          switch (uom) {
                            case 'ctn': return 'How many individual pieces are in one carton';
                            case 'bx': return 'How many individual pieces are in one box';
                            case 'pck': return 'How many individual pieces are in one pack';
                            case 'pcs': return 'Quantity per individual unit';
                            case 'grm': return 'Weight in grams per unit';
                            case 'ltr': return 'Volume in liters per unit';
                            default: return 'Container quantity information';
                          }
                        })()}
                      </p>
                    </div>

                    {/* Cost Price */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Cost Price
                      </label>
                      <div className="relative">
                        <span className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500 text-sm">₵</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={addProductForm.cost_price}
                          onChange={(e) => handleAddProductFormChange('cost_price', e.target.value)}
                          className={`w-full pl-8 pr-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                            formErrors.cost_price ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="0.00"
                        />
                      </div>
                      {formErrors.cost_price && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                          <AlertCircle size={12} className="mr-1" />
                          {formErrors.cost_price}
                        </p>
                      )}
                    </div>

                    {/* Quantity */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Quantity
                      </label>
                      <input
                        type="number"
                        min="0"
                        step="0.01"
                        value={addProductForm.quantity}
                        onChange={(e) => handleAddProductFormChange('quantity', e.target.value)}
                        className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.quantity ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="0"
                      />
                      {formErrors.quantity && (
                        <p className="text-red-500 text-xs mt-1 flex items-center">
                          <AlertCircle size={12} className="mr-1" />
                          {formErrors.quantity}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* Pricing & Inventory Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-purple-100 rounded-lg flex items-center justify-center">
                      <DollarSign size={14} className="text-purple-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Pricing & Inventory</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* Selling Price (Unit) - Always shows unit price */}
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        Selling Price (per Piece)
                        <span className="text-green-600 text-xs ml-1">✨</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">₵</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={addProductForm.selling_price_pcs}
                          onChange={(e) => handleAddProductFormChange('selling_price_pcs', e.target.value)}
                          className={`w-full pl-6 pr-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                            formErrors.selling_price_pcs ? 'border-red-500' : 'border-gray-300'
                          }`}
                          placeholder="0.00"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        Per piece
                      </p>
                      {formErrors.selling_price_pcs && (
                        <p className="text-red-500 text-xs flex items-center">
                          <AlertCircle size={10} className="mr-1" />
                          {formErrors.selling_price_pcs}
                        </p>
                      )}
                    </div>

                    {/* Selling Price (Container) - Dynamic based on UoM */}
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        {(() => {
                          const uom = addProductForm.unit_of_measure;
                          switch (uom) {
                            case 'pcs': return 'Selling Price (Bulk)';
                            case 'ctn': return 'Selling Price (per Carton)';
                            case 'bx': return 'Selling Price (per Box)';
                            case 'pck': return 'Selling Price (per Pack)';
                            case 'grm': return 'Selling Price (per kg)';
                            case 'ltr': return 'Selling Price (per liter)';
                            default: return 'Selling Price (per Container)';
                          }
                        })()}
                        <span className="text-green-600 text-xs ml-1">✨</span>
                      </label>
                      <div className="relative">
                        <span className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-500 text-xs">₵</span>
                        <input
                          type="number"
                          step="0.01"
                          min="0"
                          value={addProductForm.selling_price_ctn}
                          onChange={(e) => handleAddProductFormChange('selling_price_ctn', e.target.value)}
                          className="w-full pl-6 pr-2 py-1.5 text-sm border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                          placeholder="0.00"
                        />
                      </div>
                      <p className="text-xs text-gray-500">
                        {(() => {
                          const uom = addProductForm.unit_of_measure;
                          const pieces = addProductForm.pieces_in_ctn || 1;
                          switch (uom) {
                            case 'pcs': return `Bulk pricing`;
                            case 'ctn': return `Per carton (${pieces} pcs)`;
                            case 'bx': return `Per box (${pieces} pcs)`;
                            case 'pck': return `Per pack (${pieces} pcs)`;
                            case 'grm': return `Per kg (${pieces}g)`;
                            case 'ltr': return `Per liter (${pieces}ml)`;
                            default: return `Per container (${pieces})`;
                          }
                        })()}
                      </p>
                    </div>

                    {/* Reorder Level */}
                    <div className="space-y-1">
                      <label className="block text-xs font-medium text-gray-700">
                        Reorder Level
                      </label>
                      <input
                        type="number"
                        min="0"
                        value={addProductForm.reorder_level}
                        onChange={(e) => handleAddProductFormChange('reorder_level', e.target.value)}
                        className={`w-full px-2 py-1.5 text-sm border rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500 ${
                          formErrors.reorder_level ? 'border-red-500' : 'border-gray-300'
                        }`}
                        placeholder="5"
                      />
                      <p className="text-xs text-gray-500">
                        Min stock level
                      </p>
                      {formErrors.reorder_level && (
                        <p className="text-red-500 text-xs flex items-center">
                          <AlertCircle size={10} className="mr-1" />
                          {formErrors.reorder_level}
                        </p>
                      )}
                    </div>
                  </div>
                </div>

                {/* VAT & Description Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-orange-100 rounded-lg flex items-center justify-center">
                      <Receipt size={14} className="text-orange-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">VAT & Description</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                    {/* VAT Type */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        VAT Type
                      </label>
                      <select
                        value={addProductForm.vat_type}
                        onChange={(e) => handleAddProductFormChange('vat_type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      >
                        <option value="inclusive">VAT Inclusive</option>
                        <option value="exclusive">VAT Exclusive</option>
                        <option value="zero">Zero Rated</option>
                      </select>
                    </div>

                    {/* VAT Rate */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        VAT Rate (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={addProductForm.vat_rate}
                        onChange={(e) => handleAddProductFormChange('vat_rate', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>

                    {/* Description */}
                    <div className="lg:col-span-1">
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Description
                      </label>
                      <textarea
                        value={addProductForm.description}
                        onChange={(e) => handleAddProductFormChange('description', e.target.value)}
                        rows={3}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-none"
                        placeholder="Product description (optional)"
                      />
                    </div>
                  </div>
                </div>

                {/* Sales Information Section */}
                <div className="bg-gray-50 rounded-xl p-4">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                      <TrendingUp size={14} className="text-green-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Sales Information</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 lg:grid-cols-4 gap-4">
                    {/* Standard Margin */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Standard Margin (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={addProductForm.standard_margin}
                        onChange={(e) => handleAddProductFormChange('standard_margin', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>

                    {/* Current Margin */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Current Margin (%)
                      </label>
                      <input
                        type="number"
                        step="0.01"
                        min="0"
                        max="100"
                        value={addProductForm.current_margin}
                        onChange={(e) => handleAddProductFormChange('current_margin', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                        placeholder="0.00"
                      />
                    </div>

                    {/* Unit Cost with VAT - Read Only */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit Cost with VAT
                        <span className="text-blue-600 text-xs ml-1">📊 Calculated</span>
                      </label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
                        ₵{calculatePricing().unitCostWithVAT}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        {addProductForm.vat_type === 'inclusive' ? 'Cost price includes VAT' : 
                         addProductForm.vat_type === 'exclusive' ? 'VAT added to cost price' : 
                         'No VAT applied'}
                      </p>
                    </div>

                    {/* Unit Cost without VAT - Read Only */}
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">
                        Unit Cost without VAT
                        <span className="text-blue-600 text-xs ml-1">📊 Calculated</span>
                      </label>
                      <div className="w-full px-3 py-2 border border-gray-200 rounded-lg bg-gray-50 text-gray-600">
                        ₵{calculatePricing().unitCostWithoutVAT}
                      </div>
                      <p className="text-xs text-gray-500 mt-1">
                        Base cost before VAT calculations
                      </p>
                    </div>
                  </div>
                </div>

                {/* Pricing Summary */}
                <div className="bg-gradient-to-r from-green-50 to-blue-50 rounded-xl p-4 border border-green-200">
                  <div className="flex items-center space-x-2 mb-4">
                    <div className="w-6 h-6 bg-green-100 rounded-lg flex items-center justify-center">
                      <Calculator size={14} className="text-green-600" />
                    </div>
                    <h3 className="text-base font-semibold text-gray-900">Pricing Summary</h3>
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    {/* Cost Breakdown */}
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Cost Breakdown</h4>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>Container Cost:</span>
                          <span>{formatCurrency(parseFloat(addProductForm.cost_price) || 0, true, 'text-red-600 font-medium')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Cost per Piece:</span>
                          <span>{formatCurrency(calculatePricing().costPerPiece, true, 'text-red-500 font-medium')}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>VAT ({addProductForm.vat_rate || 0}%):</span>
                          <span>{formatCurrency(parseFloat(calculatePricing().unitCostWithVAT) - parseFloat(calculatePricing().unitCostWithoutVAT), true, 'text-orange-500 font-medium')}</span>
                        </div>
                        <div className="flex justify-between font-medium border-t pt-1">
                          <span>Cost per Piece (with VAT):</span>
                          <span>{formatCurrency(parseFloat(calculatePricing().unitCostWithVAT), true, 'text-red-600 font-semibold')}</span>
                        </div>
                      </div>
                    </div>

                    {/* Margin Analysis */}
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Margin Analysis</h4>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>Standard Margin:</span>
                          <span>{addProductForm.standard_margin || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Current Margin:</span>
                          <span className="font-medium text-green-600">{addProductForm.current_margin || 0}%</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Profit per Unit:</span>
                          <span>₵{(parseFloat(calculatePricing().sellingPricePcs) - parseFloat(calculatePricing().unitCostWithVAT)).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>

                    {/* Unit Pricing */}
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">Unit Pricing</h4>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>Cost per Unit:</span>
                          <span>₵{calculatePricing().unitCostWithVAT}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Selling Price:</span>
                          <span className="font-medium text-blue-600">₵{calculatePricing().sellingPricePcs}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Markup:</span>
                          <span className="text-green-600">+{addProductForm.current_margin || 0}%</span>
                        </div>
                      </div>
                    </div>

                    {/* Dynamic Container Pricing */}
                    <div className="bg-white rounded-lg p-3 border border-gray-200">
                      <h4 className="text-sm font-medium text-gray-700 mb-2">
                        {(() => {
                          const uom = addProductForm.unit_of_measure;
                          switch (uom) {
                            case 'pcs': return 'Bulk Pricing';
                            case 'ctn': return 'Carton Pricing';
                            case 'bx': return 'Box Pricing';
                            case 'pck': return 'Pack Pricing';
                            case 'grm': return 'Kilogram Pricing';
                            case 'ltr': return 'Liter Pricing';
                            default: return 'Container Pricing';
                          }
                        })()}
                      </h4>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span>
                            {(() => {
                              const uom = addProductForm.unit_of_measure;
                              switch (uom) {
                                case 'pcs': return 'Units per Bulk:';
                                case 'ctn': return 'Pieces per Carton:';
                                case 'bx': return 'Pieces per Box:';
                                case 'pck': return 'Pieces per Pack:';
                                case 'grm': return 'Grams per Unit:';
                                case 'ltr': return 'Liters per Unit:';
                                default: return 'Units per Container:';
                              }
                            })()}
                          </span>
                          <span>{addProductForm.pieces_in_ctn || 1}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>
                            {(() => {
                              const uom = addProductForm.unit_of_measure;
                              switch (uom) {
                                case 'pcs': return 'Bulk Price:';
                                case 'ctn': return 'Carton Price:';
                                case 'bx': return 'Box Price:';
                                case 'pck': return 'Pack Price:';
                                case 'grm': return 'Kilogram Price:';
                                case 'ltr': return 'Liter Price:';
                                default: return 'Container Price:';
                              }
                            })()}
                          </span>
                          <span className="font-medium text-purple-600">₵{calculatePricing().sellingPriceCtn}</span>
                        </div>
                        <div className="flex justify-between">
                          <span>Total Profit:</span>
                          <span className="text-green-600">₵{((parseFloat(calculatePricing().sellingPricePcs) - parseFloat(calculatePricing().unitCostWithVAT)) * (addProductForm.pieces_in_ctn || 1)).toFixed(2)}</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

            </form>
          </div>
        </div>
      )}

      {/* Image Preview Modal */}
      {showImageModal && selectedImage && (
        <div className="fixed inset-0 bg-black bg-opacity-75 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden">
            <div className="p-6 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Product Image</h2>
                  <p className="text-gray-600 mt-1">{selectedImage.name}</p>
                  <p className="text-sm text-gray-500">ID: {selectedImage.productID}</p>
                </div>
                <button
                  onClick={handleCloseImageModal}
                  className="px-3 py-1 bg-red-500 hover:bg-red-600 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  Close
                </button>
              </div>
            </div>
            
            <div className="p-6">
              <div className="flex items-center justify-center">
                <img 
                  src={selectedImage.url} 
                  alt={selectedImage.name}
                  className="max-w-full max-h-[60vh] object-contain rounded-lg shadow-lg"
                  onError={(e) => {
                    e.target.style.display = 'none';
                    e.target.nextSibling.style.display = 'flex';
                  }}
                />
                <div className="hidden w-full h-64 bg-gray-100 rounded-lg">
                  <div className="text-center">
                    <Package size={48} className="text-gray-400 mx-auto mb-4" />
                    <p className="text-gray-500">Image failed to load</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* View Product Modal */}
      {showViewModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-3xl shadow-2xl max-w-5xl w-full max-h-[95vh] overflow-hidden">
            {/* Header with gradient background */}
            <div className="relative bg-gradient-to-br from-indigo-600 via-purple-600 to-pink-600 px-8 py-6 text-white overflow-hidden">
              <div className="absolute inset-0 bg-black bg-opacity-10"></div>
              <div className="relative flex items-center justify-between">
                <div className="flex items-center space-x-4">
                  <div className="w-16 h-16 bg-white bg-opacity-20 rounded-2xl flex items-center justify-center backdrop-blur-sm">
                    <Eye size={28} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold">Product Details</h2>
                    <p className="text-white text-opacity-90 text-sm mt-1">Complete product information overview</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    onClick={handleCloseViewModal}
                    className="px-4 py-2 text-white bg-white bg-opacity-10 hover:bg-opacity-20 backdrop-blur-sm border border-white border-opacity-20 rounded-xl transition-all duration-200 font-medium text-sm hover:scale-105 active:scale-95"
                  >
                    Close
                  </button>
                  <button
                    onClick={() => {
                      handleCloseViewModal();
                      handleEditProduct(selectedProduct);
                    }}
                    className="px-5 py-2 bg-white text-blue-600 hover:bg-blue-50 rounded-xl transition-all duration-200 flex items-center gap-2 font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 border border-blue-100"
                  >
                    <Edit size={16} />
                    Edit Product
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-8 overflow-y-auto max-h-[calc(95vh-120px)]">
              {/* Product Image Section */}
              {selectedProduct.image_url && (
                <div className="mb-8">
                  <div className="flex justify-center">
                    <div className="relative group">
                      <img 
                        src={selectedProduct.image_url} 
                        alt={selectedProduct.name}
                        className="w-64 h-64 object-cover rounded-2xl shadow-2xl border-4 border-white transform group-hover:scale-105 transition-transform duration-300"
                        onError={(e) => {
                          console.warn('Product image failed to load:', selectedProduct.image_url);
                          e.target.style.display = 'none';
                        }}
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent rounded-2xl opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                    </div>
                  </div>
                </div>
              )}

              {/* Product Header Info */}
              <div className="text-center mb-8">
                <h1 className="text-3xl font-bold text-gray-900 mb-2">{selectedProduct.name}</h1>
                <div className="flex items-center justify-center space-x-4 text-gray-600">
                  <span className="bg-gray-100 px-3 py-1 rounded-full text-sm font-mono">{selectedProduct.productID}</span>
                  <span className="bg-blue-100 text-blue-800 px-3 py-1 rounded-full text-sm font-medium">
                    {selectedProduct.category?.main_cat || 'Uncategorized'}
                  </span>
                  <span className="bg-green-100 text-green-800 px-3 py-1 rounded-full text-sm font-medium">
                    {formatQuantity(selectedProduct.quantity || 0)} {selectedProduct.unit_of_measure}
                  </span>
                </div>
              </div>

              {/* Information Cards Grid */}
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Basic Information Card */}
                <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl p-6 border border-blue-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-blue-500 rounded-xl flex items-center justify-center">
                      <Package size={20} className="text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Basic Information</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Product Name</label>
                      <p className="text-gray-900 font-semibold">{selectedProduct.name}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Product ID</label>
                      <p className="text-gray-900 font-mono text-sm bg-white px-2 py-1 rounded">{selectedProduct.productID}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Category</label>
                      <p className="text-gray-900">
                        {selectedProduct.category?.main_cat || 'Uncategorized'} 
                        {selectedProduct.category?.sub_cat && ` - ${selectedProduct.category.sub_cat}`}
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Description</label>
                      <p className="text-gray-900 text-sm">{selectedProduct.description || 'No description available'}</p>
                    </div>
                  </div>
                </div>

                {/* Pricing Information Card */}
                <div className="bg-gradient-to-br from-green-50 to-emerald-50 rounded-2xl p-6 border border-green-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-green-500 rounded-xl flex items-center justify-center">
                      <DollarSign size={20} className="text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Pricing Information</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Cost Price</label>
                      <p className="text-2xl font-bold text-red-600">{formatCurrency(parseFloat(selectedProduct.cost_price) || 0)}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Unit Price</label>
                      <p className="text-xl font-bold text-gray-900">
                        {formatCurrency(parseFloat(selectedProduct.selling_price_pcs) || 0, true, 'text-green-600')} 
                        <span className="text-sm text-gray-500 ml-1">/ {selectedProduct.unit_of_measure}</span>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Carton Price</label>
                      <p className="text-lg font-semibold text-gray-900">
                        {formatCurrency(parseFloat(selectedProduct.selling_price_ctn) || 0, true, 'text-blue-600')} 
                        <span className="text-sm text-gray-500 ml-1">/ ctn</span>
                      </p>
                    </div>
                    <div className="flex space-x-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">VAT Type</label>
                        <span className="bg-purple-100 text-purple-800 px-2 py-1 rounded-full text-xs font-medium capitalize">
                          {selectedProduct.vat_type}
                        </span>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-600 mb-1">VAT Rate</label>
                        <span className="bg-orange-100 text-orange-800 px-2 py-1 rounded-full text-xs font-medium">
                          {selectedProduct.vat_rate}%
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Inventory Information Card */}
                <div className="bg-gradient-to-br from-orange-50 to-red-50 rounded-2xl p-6 border border-orange-100">
                  <div className="flex items-center space-x-3 mb-4">
                    <div className="w-10 h-10 bg-orange-500 rounded-xl flex items-center justify-center">
                      <Package size={20} className="text-white" />
                    </div>
                    <h3 className="text-lg font-bold text-gray-900">Inventory Information</h3>
                  </div>
                  <div className="space-y-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Current Stock</label>
                      <p className="text-2xl font-bold text-orange-600">
                        {formatQuantity(selectedProduct.quantity || 0)} 
                        <span className="text-sm text-gray-500 ml-1">{selectedProduct.unit_of_measure}</span>
                      </p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Reorder Level</label>
                      <p className="text-lg font-semibold text-gray-900">{selectedProduct.reorder_level || 0}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Pieces per Container</label>
                      <p className="text-lg font-semibold text-gray-900">{selectedProduct.pieces_in_ctn || 0}</p>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-600 mb-1">Unit of Measure</label>
                      <span className="bg-blue-100 text-blue-800 px-2 py-1 rounded-full text-xs font-medium">
                        {selectedProduct.unit_of_measure}
                      </span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Additional Information */}
              <div className="mt-8 bg-gradient-to-br from-gray-50 to-slate-50 rounded-2xl p-6 border border-gray-100">
                <div className="flex items-center space-x-3 mb-4">
                  <div className="w-10 h-10 bg-gray-500 rounded-xl flex items-center justify-center">
                    <Info size={20} className="text-white" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">Additional Information</h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Standard Margin</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedProduct.standard_margin || 0}%</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Current Margin</label>
                    <p className="text-lg font-semibold text-gray-900">{selectedProduct.current_margin || 0}%</p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-600 mb-1">Created Date</label>
                    <p className="text-lg font-semibold text-gray-900">
                      {new Date(selectedProduct.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
              </div>
            </div>

            {/* Footer with last updated info */}
            <div className="bg-gray-50 border-t border-gray-200 px-8 py-4">
              <div className="text-sm text-gray-500 text-center">
                Last updated: {new Date(selectedProduct.updated_at).toLocaleDateString()}
              </div>
            </div>
          </div>
        </div>
      )}


      {/* Delete Product Modal */}
      {showDeleteModal && selectedProduct && (
        <div className="fixed inset-0 bg-black bg-opacity-60 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full">
            <div className="bg-gradient-to-r from-red-500 to-pink-600 px-6 py-4 text-white">
              <div className="flex items-center justify-between">
                <div className="flex items-center space-x-3">
                  <div className="w-10 h-10 bg-white bg-opacity-20 rounded-lg flex items-center justify-center">
                    <Trash2 size={20} className="text-white" />
                  </div>
                  <div>
                    <h2 className="text-xl font-bold">Delete Product</h2>
                    <p className="text-white text-opacity-80 text-sm">This action cannot be undone</p>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <button
                    type="button"
                    onClick={handleCloseDeleteModal}
                    className="px-4 py-2 text-white bg-white bg-opacity-10 hover:bg-opacity-20 backdrop-blur-sm border border-white border-opacity-20 rounded-xl transition-all duration-200 font-medium text-sm hover:scale-105 active:scale-95"
                  >
                    Cancel
                  </button>
                  <button
                    type="button"
                    onClick={handleConfirmDelete}
                    disabled={deleteProductLoading}
                    className="px-5 py-2 bg-white text-red-600 hover:bg-red-50 rounded-xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed font-semibold text-sm shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 border border-red-100"
                  >
                    {deleteProductLoading ? (
                      <>
                        <Loader2 size={16} className="animate-spin" />
                        Deleting...
                      </>
                    ) : (
                      <>
                        <Trash2 size={16} />
                        Delete Product
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
            
            <div className="p-6">
              <div className="text-center">
                <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
                  <Trash2 size={32} className="text-red-600" />
                </div>
                <h3 className="text-lg font-semibold text-gray-900 mb-2">Are you sure?</h3>
                <p className="text-gray-600 mb-4">
                  You are about to delete <strong>{selectedProduct.name}</strong> (ID: {selectedProduct.productID}).
                  This action cannot be undone and will permanently remove the product from your inventory.
                </p>
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 mb-6">
                  <div className="flex items-center">
                    <AlertCircle size={20} className="text-yellow-600 mr-2" />
                    <p className="text-yellow-800 text-sm">
                      <strong>Warning:</strong> This will also remove all associated sales history and inventory records.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            
          </div>
        </div>
      )}
    </Layout>
  );
};

export default ProductList;
